import { useEffect, useState } from 'react';
import api from '../../services/api';
import './PrintLabel.css';

/**
 * Standalone print-label page.
 * Opened in a new tab by ProductSummaryTable's print button.
 *
 * Supports two modes depending on URL params:
 *
 * ── HTML label mode (new) ────────────────────────────────────────
 *   labelHtml  — full path to the label-html endpoint, e.g.:
 *                /api/employee/variants/14/label-html/?customer=Acme
 *
 *   The endpoint returns a fully rendered HTML page sized for 4"×6".
 *   We load it into a full-page <iframe> so the browser prints it
 *   at the correct dimensions.
 *
 * ── Image blob mode (legacy) ────────────────────────────────────
 *   url      — label image path (e.g. /api/employee/variants/14/label/)
 *   qty      — number of copies to print
 *   variant  — variant name
 *   product  — product name
 */
const PrintLabel = () => {
  const params       = new URLSearchParams(window.location.search);
  const labelHtmlPath = params.get('labelHtml') || '';   // HTML mode
  const apiPath      = params.get('url') || '';           // legacy image mode
  const qty          = parseInt(params.get('qty'), 10) || 1;
  const variant      = params.get('variant') || '';
  const product      = params.get('product') || '';

  // ── HTML label mode state ──
  const [htmlSrc, setHtmlSrc]     = useState('');  // blob URL for iframe srcdoc
  const [htmlReady, setHtmlReady] = useState(false);
  const [loadError, setLoadError] = useState('');

  // ── Legacy image mode state ──
  const [blobUrl, setBlobUrl] = useState('');

  // ── HTML mode: fetch rendered HTML with auth and load into iframe ──
  useEffect(() => {
    if (!labelHtmlPath) return;

    // Strip /api prefix so axios appends to its baseURL correctly
    const relativePath = labelHtmlPath.startsWith('/api/')
      ? labelHtmlPath.slice(4)
      : labelHtmlPath;

    // Track the created URL in the closure so the cleanup can revoke it
    // even before the state update has flushed.
    let createdUrl = '';

    api.get(relativePath, { responseType: 'text' })
      .then((response) => {
        const blob = new Blob([response.data], { type: 'text/html;charset=utf-8' });
        createdUrl = URL.createObjectURL(blob);
        setHtmlSrc(createdUrl);
      })
      .catch(() => {
        setLoadError('Could not load label. Make sure you are logged in.');
      });

    return () => {
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [labelHtmlPath]);

  // Auto-print once iframe signals it's ready
  useEffect(() => {
    if (!htmlReady) return;
    const timer = setTimeout(() => window.print(), 600);
    return () => clearTimeout(timer);
  }, [htmlReady]);

  // ── Legacy mode: fetch image blob with auth ──
  useEffect(() => {
    if (!apiPath) return;

    const relativePath = apiPath.startsWith('/api/')
      ? apiPath.slice(4)
      : apiPath;

    let objectUrl = '';
    api.get(relativePath, { responseType: 'blob' })
      .then((response) => {
        objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        setLoadError('Could not load label image. Make sure you are logged in.');
      });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [apiPath]);

  // Auto-print once legacy image is ready
  useEffect(() => {
    if (!blobUrl) return;
    const timer = setTimeout(() => window.print(), 800);
    return () => clearTimeout(timer);
  }, [blobUrl]);

  // ── Error state ──
  if (loadError) {
    return (
      <div className="print-label-page print-label print-label--error">
        <p>{loadError}</p>
      </div>
    );
  }

  // ── No params ──
  if (!labelHtmlPath && !apiPath) {
    return (
      <div className="print-label-page print-label print-label--error">
        <p>No label URL provided.</p>
      </div>
    );
  }

  // ── HTML label mode ──
  if (labelHtmlPath) {
    if (!htmlSrc) {
      return (
        <div className="print-label-page print-label print-label--loading">
          <p>Loading label…</p>
        </div>
      );
    }

    return (
      <div className="print-label-page print-label print-label--html">
        {/* Screen-only toolbar */}
        <div className="print-label__toolbar no-print">
          <button
            type="button"
            className="print-label__print-btn"
            onClick={() => window.print()}
          >
            🖨 Print
          </button>
        </div>
        {/* Full-page iframe containing the rendered 4×6 label */}
        <iframe
          src={htmlSrc}
          className="print-label__iframe"
          title="Product Label"
          onLoad={() => setHtmlReady(true)}
        />
      </div>
    );
  }

  // ── Legacy image mode ──
  if (!blobUrl) {
    return (
      <div className="print-label-page print-label print-label--loading">
        <p>Loading label…</p>
      </div>
    );
  }

  return (
    <div className="print-label-page print-label">
      {/* Screen-only header (hidden in print) */}
      <div className="print-label__header no-print">
        <div className="print-label__meta">
          <span className="print-label__product">{product}</span>
          {variant && <span className="print-label__variant">{variant}</span>}
        </div>
        <div className="print-label__controls">
          <span className="print-label__copies">
            {qty} {qty === 1 ? 'copy' : 'copies'} — set in print dialog
          </span>
          <button
            type="button"
            className="print-label__print-btn"
            onClick={() => window.print()}
          >
            🖨 Print
          </button>
        </div>
      </div>

      {/* The label image — this is what gets printed */}
      <div className="print-label__image-wrap">
        <img
          src={blobUrl}
          alt={`Label for ${product}${variant ? ` – ${variant}` : ''}`}
          className="print-label__image"
        />
      </div>

      {/* Screen-only footer hint */}
      <p className="print-label__hint no-print">
        Set <strong>Copies</strong> to <strong>{qty}</strong> in the print dialog.
      </p>
    </div>
  );
};

export default PrintLabel;
